/**
 * OneDrive Service - Delegated Authentication
 * Service for managing Excel files in OneDrive using user authentication
 * User authenticates once via OAuth, tokens stored in Supabase database
 */

import { getNetlifyFunctionUrl } from '../config/config';

const FUNCTION_NAME = 'onedrive-app-auth';

/**
 * Check if user is authenticated (checks database via backend)
 */
export const isAuthenticated = async () => {
  try {
    const url = getNetlifyFunctionUrl(FUNCTION_NAME);
    
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        action: 'checkAuth',
      }),
    });

    if (!response.ok) {
      return false;
    }

    const data = await response.json();
    return data.isAuthenticated;
  } catch (error) {
    console.error('Error checking authentication:', error);
    return false;
  }
};

/**
 * Get configuration from server (environment-specific)
 * @param {string} fileType - Optional: Type of file to get specific path
 * @returns {Promise<Object>} Configuration object with folder paths
 */
export const getConfig = async (fileType = null) => {
  try {
    const url = getNetlifyFunctionUrl(FUNCTION_NAME);
    
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        action: 'getConfig',
        fileType,
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to get configuration');
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error getting configuration:', error);
    throw error;
  }
};

/**
 * Get user information and OneDrive configuration
 */
export const getUserInfo = async () => {
  try {
    const url = getNetlifyFunctionUrl(FUNCTION_NAME);
    
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        action: 'getUserInfo',
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to get user info');
    }

    const data = await response.json();
    return {
      email: data.userInfo.email,
      displayName: data.userInfo.displayName,
      folderPath: data.folderPath,
      tokenCreatedAt: data.tokenCreatedAt,
    };
  } catch (error) {
    console.error('Error getting user info:', error);
    throw error;
  }
};

/**
 * Get authentication URL
 */
export const getAuthUrl = async () => {
  try {
    const url = getNetlifyFunctionUrl(FUNCTION_NAME);
    
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        action: 'getAuthUrl',
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to get auth URL');
    }

    const data = await response.json();
    return data.authUrl;
  } catch (error) {
    console.error('Error getting auth URL:', error);
    throw error;
  }
};

/**
 * Complete authentication with authorization code
 * Tokens are stored in database by backend
 */
export const completeAuthentication = async (code) => {
  try {
    const url = getNetlifyFunctionUrl(FUNCTION_NAME);
    
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        action: 'authenticate',
        code,
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to authenticate');
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error completing authentication:', error);
    throw error;
  }
};

/**
 * Logout - delete tokens from database
 */
export const logout = async () => {
  try {
    const url = getNetlifyFunctionUrl(FUNCTION_NAME);
    
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        action: 'logout',
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to logout');
    }

    return await response.json();
  } catch (error) {
    console.error('Error logging out:', error);
    throw error;
  }
};

/**
 * List all Excel files in OneDrive folder
 * @param {string} fileType - Type of file: 'presupuesto', 'produccion', 'gastos', or 'preparativos'
 * @param {string} customPath - Optional custom folder path (for preparativos with dynamic paths)
 * @returns {Promise<Array>} Array of file objects
 */
export const listFiles = async (fileType = 'presupuesto', customPath = null) => {
  try {
    const url = getNetlifyFunctionUrl(FUNCTION_NAME);
    
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        action: 'list',
        fileType,
        customPath,
      }),
    });

    if (!response.ok) {
      let errorMessage = 'Failed to list files';
      try {
        const error = await response.json();
        errorMessage = error.error || errorMessage;
      } catch (e) {
        // Response is not JSON (probably HTML error page)
        const text = await response.text();
        console.error('Non-JSON response:', text.substring(0, 200));
        errorMessage = `Server error (${response.status}): La función de Netlify no está disponible`;
      }
      throw new Error(errorMessage);
    }

    const data = await response.json();
    return data.files;
  } catch (error) {
    console.error('Error listing files:', error);
    throw error;
  }
};

/**
 * Upload a file to OneDrive
 * @param {File} file - File object to upload
 * @param {string} fileType - Type of file: 'presupuesto', 'produccion', 'gastos', or 'preparativos'
 * @param {string} customPath - Optional custom folder path (for preparativos with dynamic paths)
 * @returns {Promise<Object>} Uploaded file metadata
 */
export const uploadFile = async (file, fileType = 'presupuesto', customPath = null) => {
  try {
    // Convert file to base64
    const fileContent = await fileToBase64(file);
    
    const url = getNetlifyFunctionUrl(FUNCTION_NAME);
    
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        action: 'upload',
        fileName: file.name,
        fileContent: fileContent.split(',')[1], // Remove data:... prefix
        fileType,
        customPath,
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to upload file');
    }

    const data = await response.json();
    return data.file;
  } catch (error) {
    console.error('Error uploading file:', error);
    throw error;
  }
};

/**
 * Download a file from OneDrive
 * @param {string} fileId - ID of the file to download
 * @param {string} fileName - Name of the file
 * @returns {Promise<void>}
 */
export const downloadFile = async (fileId, fileName) => {
  try {
    const url = getNetlifyFunctionUrl(FUNCTION_NAME);
    
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        action: 'download',
        fileId,
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to download file');
    }

    const data = await response.json();
    
    // Convert base64 to blob and trigger download
    const blob = base64ToBlob(data.fileData, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);
  } catch (error) {
    console.error('Error downloading file:', error);
    throw error;
  }
};

/**
 * Download a file from OneDrive as Blob (for email attachments)
 * @param {string} fileId - ID of the file to download
 * @returns {Promise<Blob>} File content as Blob
 */
export const downloadFileAsBlob = async (fileId) => {
  try {
    const url = getNetlifyFunctionUrl(FUNCTION_NAME);
    
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        action: 'download',
        fileId,
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to download file');
    }

    const data = await response.json();
    
    // Convert base64 to blob and return
    const blob = base64ToBlob(data.fileData, data.mimeType || 'application/octet-stream');
    return blob;
  } catch (error) {
    console.error('Error downloading file as blob:', error);
    throw error;
  }
};

/**
 * Delete a file from OneDrive
 * @param {string} fileId - ID of the file to delete
 * @returns {Promise<Object>} Success response
 */
export const deleteFile = async (fileId) => {
  try {
    const url = getNetlifyFunctionUrl(FUNCTION_NAME);
    
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        action: 'delete',
        fileId,
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to delete file');
    }

    return await response.json();
  } catch (error) {
    console.error('Error deleting file:', error);
    throw error;
  }
};

/**
 * Get file metadata
 * @param {string} fileId - ID of the file
 * @returns {Promise<Object>} File metadata
 */
export const getFileMetadata = async (fileId) => {
  try {
    const url = getNetlifyFunctionUrl(FUNCTION_NAME);
    
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        action: 'metadata',
        fileId,
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to get file metadata');
    }

    const data = await response.json();
    return data.file;
  } catch (error) {
    console.error('Error getting file metadata:', error);
    throw error;
  }
};

/**
 * Helper: Convert File to base64
 * @param {File} file - File object
 * @returns {Promise<string>} Base64 string
 */
const fileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });
};

/**
 * Helper: Convert base64 to Blob
 * @param {string} base64 - Base64 string
 * @param {string} contentType - MIME type
 * @returns {Blob} Blob object
 */
const base64ToBlob = (base64, contentType) => {
  const byteCharacters = atob(base64);
  const byteNumbers = new Array(byteCharacters.length);
  
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  
  const byteArray = new Uint8Array(byteNumbers);
  return new Blob([byteArray], { type: contentType });
};

/**
 * Format file size for display
 * @param {number} bytes - File size in bytes
 * @returns {string} Formatted size
 */
export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
};

/**
 * Format date for display
 * @param {string} dateString - ISO date string
 * @returns {string} Formatted date
 */
export const formatDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleString('es-CL', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

/**
 * Set a file as the vigente (active) file
 * @param {string} fileId - OneDrive file ID
 * @param {string} fileType - Type of file: 'presupuesto', 'produccion', or 'gastos'
 * @returns {Promise<Object>} Response from server
 */
export const setVigente = async (fileId, fileType = 'presupuesto') => {
  try {
    const url = getNetlifyFunctionUrl(FUNCTION_NAME);
    
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        action: 'setVigente',
        fileId: fileId,
        fileType,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Error al marcar archivo como vigente');
    }

    return await response.json();
  } catch (error) {
    console.error('Error setting vigente:', error);
    throw error;
  }
};

/**
 * Get the current vigente (active) file
 * @param {string} fileType - Type of file: 'presupuesto', 'produccion', or 'gastos'
 * @returns {Promise<Object|null>} Vigente file or null
 */
export const getVigente = async (fileType = 'presupuesto') => {
  try {
    const url = getNetlifyFunctionUrl(FUNCTION_NAME);
    
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        action: 'getVigente',
        fileType,
      }),
    });

    if (!response.ok) {
      let errorMessage = `Error al obtener ${fileType} vigente`;
      try {
        const errorData = await response.json();
        errorMessage = errorData.error || errorMessage;
      } catch (e) {
        // Response is not JSON (probably HTML error page)
        const text = await response.text();
        console.error('Non-JSON response:', text.substring(0, 200));
        errorMessage = `Server error (${response.status}): La función de Netlify no está disponible`;
      }
      throw new Error(errorMessage);
    }

    const data = await response.json();
    return data.archivo || null;
  } catch (error) {
    console.error('Error getting vigente:', error);
    throw error;
  }
};

/**
 * Read Excel file from OneDrive and parse it
 * @param {string} fileId - OneDrive file ID
 * @param {string} sheetName - Name of the sheet to read (default: '2026')
 * @returns {Promise<Object>} Parsed Excel data with formatting
 */
export const readExcelFile = async (fileId, sheetName = '2026') => {
  try {
    const url = getNetlifyFunctionUrl(FUNCTION_NAME);
    
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        action: 'readExcel',
        fileId: fileId,
        sheetName: sheetName
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Error al leer archivo Excel');
    }

    const data = await response.json();
    return data.excelData;
  } catch (error) {
    console.error('Error reading Excel file:', error);
    throw error;
  }
};
